/**
 * Encrypts / decrypts raise-complaint-checklist query strings (AES-256-CBC).
 * Algorithm matches CSRP FieldEncryptionHelper (passphrase-derived key/IV).
 *
 * URL shape: /raise-complaint-checklist?q=ENC:<base64-ciphertext>
 * Plaintext inside ciphertext: invoiceNo=...&productCode=...&srCode=...
 */

export const ENCRYPTED_QUERY_PARAM = "q";
const ENC_PREFIX = "ENC:";

const DEFAULT_PASSPHRASE =
  process.env.REACT_APP_QUERY_ENCRYPTION_PASSPHRASE ||
  "CSRP-FieldEncryption-Default-Passphrase-2026";

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function bytesToBase64(bytes) {
  let binary = "";
  const arr = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes;
  for (let i = 0; i < arr.length; i += 1) {
    binary += String.fromCharCode(arr[i]);
  }
  return btoa(binary);
}

function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Same derivation as FieldEncryptionHelper.DeriveKeyIvFromPassphrase */
async function deriveKeyIv(passphrase = DEFAULT_PASSPHRASE) {
  const keyBuffer = await crypto.subtle.digest(
    "SHA-256",
    textEncoder.encode(passphrase)
  );
  const ivFull = await crypto.subtle.digest(
    "SHA-256",
    textEncoder.encode(`${passphrase}|iv`)
  );
  const key = await crypto.subtle.importKey(
    "raw",
    keyBuffer,
    { name: "AES-CBC", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
  const iv = ivFull.slice(0, 16);
  return { key, iv };
}

export function isEncryptedPayload(value) {
  return (
    typeof value === "string" &&
    value.trim().toUpperCase().startsWith(ENC_PREFIX.toUpperCase())
  );
}

/**
 * @param {string} plainText
 * @returns {Promise<string>} ENC:<base64>
 */
export async function encryptPlaintext(plainText) {
  const { key, iv } = await deriveKeyIv();
  const cipher = await crypto.subtle.encrypt(
    { name: "AES-CBC", iv },
    key,
    textEncoder.encode(plainText)
  );
  return `${ENC_PREFIX}${bytesToBase64(cipher)}`;
}

/**
 * @param {string} payload ENC:<base64> or raw base64
 * @returns {Promise<string>} decrypted query string
 */
export async function decryptPayload(payload) {
  if (!payload) return "";
  const trimmed = String(payload).trim();
  if (!isEncryptedPayload(trimmed)) return trimmed;

  const b64 = trimmed.slice(ENC_PREFIX.length);
  const { key, iv } = await deriveKeyIv();
  const plainBuffer = await crypto.subtle.decrypt(
    { name: "AES-CBC", iv },
    key,
    base64ToBytes(b64)
  );
  return textDecoder.decode(plainBuffer);
}

/**
 * @param {string} routePath e.g. "/bcm-service-requests"
 * @param {URLSearchParams|Record<string, string>} params
 * @returns {Promise<string>} path + search, e.g. /bcm-service-requests?q=ENC:...
 */
export async function buildEncryptedPath(routePath, params) {
  const base = String(routePath || "").replace(/\/+$/, "") || "/";
  const sp =
    params instanceof URLSearchParams
      ? params
      : new URLSearchParams(params);
  const qs = sp.toString();
  if (!qs) return base;
  const encrypted = await encryptPlaintext(qs);
  const q = new URLSearchParams();
  q.set(ENCRYPTED_QUERY_PARAM, encrypted);
  return `${base}?${q.toString()}`;
}

/** @deprecated Use buildEncryptedPath("/raise-complaint-checklist", params) */
export async function buildEncryptedChecklistPath(params) {
  return buildEncryptedPath("/raise-complaint-checklist", params);
}

/**
 * Resolves react-router search params (encrypted `q` or legacy plain params).
 * @param {URLSearchParams} rawParams
 * @returns {Promise<URLSearchParams>}
 */
export async function resolveEncryptedSearchParams(rawParams) {
  const encryptedQ = rawParams.get(ENCRYPTED_QUERY_PARAM);
  if (encryptedQ) {
    try {
      const plain = await decryptPayload(encryptedQ);
      if (plain && plain.includes("=")) {
        return new URLSearchParams(plain);
      }
    } catch (err) {
      console.error("Failed to decrypt URL query:", err);
      return new URLSearchParams();
    }
  }

  const legacy = new URLSearchParams();
  for (const [key, value] of rawParams.entries()) {
    if (key !== ENCRYPTED_QUERY_PARAM) {
      legacy.append(key, value);
    }
  }
  return legacy.toString() ? legacy : rawParams;
}
